import { Backdrop } from "@material-ui/core";
import { Color } from "@material-ui/lab/Alert";
import { isStaffRole } from "@saleor/auth/hooks/useUserRole";
import { showNotifyMessage } from "@saleor/changeOrder/utils";
import MyTypography from "@saleor/components/MyTypography";
import SnackbarAlert from "@saleor/components/SnackbarAlert";
import { defaultListSettings } from "@saleor/config";
import DialogCreateOrderError from "@saleor/createOrder/components/DialogCreateOrderError";
import { orderCartlUrl } from "@saleor/createOrder/urls";
import ContractNoExport from "@saleor/createOrderExport/components/ContractNo";
import DialogError from "@saleor/createOrderExport/components/DialogError";
import { ExportPiQueryParams } from "@saleor/createOrderExport/urls";
import {
  ExportPiProductsSortField,
  OrderDirection,
  ScgpExportErrorCode,
  useCreateExportCartMutation,
  useCreateExportOrderMutation,
  useExportPiQuery,
  useTotalCountExportCartQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import useLocalPaginator, {
  useLocalPaginationState
} from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { makeStyles } from "@saleor/macaw-ui";
import { ListViews } from "@saleor/types";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import BottomBar from "./BottomBar";
import TableDetail from "./TableDetail";

interface ContractDetailProps {
  id: string;
  params: ExportPiQueryParams;
}

const useStyles = makeStyles(
  theme => ({
    snackHeadTitle: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      columnGap: "1.2rem"
    },
    backDrop: {
      zIndex: 99
    },
    inlineBlock: {
      display: "inline-block",
      color: theme.palette.grey[900]
    }
  }),
  { name: "ContractDetail" }
);

const ContractDetail: React.FC<ContractDetailProps> = props => {
  const { id } = props;
  const [listProductState, setListProductState] = useState<any[]>([]);
  const [listProductChange, setListProductChange] = useState<any[]>([]);
  const [openDialogError, setOpenDialogError] = useState<boolean>(false);
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [typeAlert, setTypeAlert] = useState<Color>("success");
  const navigate = useNavigator();
  const classes = useStyles();
  const intl = useIntl();
  const [, dispatchAppState] = useAppState();
  const [openCreateOrderError, setOpenCreateOrderError] = useState<boolean>(
    false
  );

  const [popUpContent, setPopUpContent] = useState(null);

  const notify = useNotifier();
  const isStaff = isStaffRole();

  // Paging
  const { updateListSettings, settings } = useListSettings(
    ListViews.EXPORT_PI_LIST
  );
  useEffect(() => {
    updateListSettings("rowNumber", defaultListSettings.SOLD_TO_LIST.rowNumber);
  }, []);
  const [
    valuesPaginationState,
    setValuesPaginationState
  ] = useLocalPaginationState(settings?.rowNumber);

  const resetPaginationState = () => {
    setValuesPaginationState({
      first: settings?.rowNumber,
      after: undefined,
      before: undefined
    });
  };

  const paginateValues = useLocalPaginator(setValuesPaginationState);

  const queryVariables = React.useMemo(
    () => ({
      ...valuesPaginationState,
      id,
      sortBy: {
        direction: OrderDirection.ASC,
        field: ExportPiProductsSortField.ITEM_NO
      }
    }),
    [settings?.rowNumber, valuesPaginationState]
  );

  const { data, loading, refetch: refetchExportPi } = useExportPiQuery({
    skip: !id,
    variables: queryVariables
  });

  const { refetch: refetchCartExportCount } = useTotalCountExportCartQuery({
    skip: true
  });

  const reCountCartExport = response => {
    if (response?.data?.exportCarts?.totalCartItem) {
      const orderCount = response?.data?.exportCarts?.totalCartItem || 0;
      dispatchAppState({
        payload: {
          orderCount
        },
        type: "setOrderCount"
      });
    }
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginateValues(
    data?.exportPi?.piProducts?.pageInfo,
    valuesPaginationState
  );

  const lastPaginationState = () => {
    setValuesPaginationState({
      last: data?.exportPi?.piProducts?.latestPageItemNumber
    });
  };

  const handlePreviousFirstPage = () => {
    beforeChangePage();
    resetPaginationState();
  };
  const handleNextLastPage = () => {
    beforeChangePage();
    lastPaginationState();
  };

  useEffect(() => {
    resetPaginationState();
  }, [settings?.rowNumber]);

  useEffect(() => {
    if (data && !loading) {
      const edgeNode = mapEdgesToItems(data?.exportPi?.piProducts);
      const listProduct =
        edgeNode?.map(edge => ({
          ...edge,
          selected: false,
          errorQuantity: false,
          quantity: "",
          writable: true
        })) || [];

      listProduct.forEach(line => {
        const lineChange = listProductChange.find(
          change => change.id === line.id
        );
        if (lineChange) {
          line.selected = lineChange.selected;
          line.errorQuantity = lineChange.errorQuantity;
          line.quantity = lineChange.quantity;
        }
      });
      setListProductState(listProduct);
    }
  }, [data, loading]);

  const beforeChangePage = () => {
    const listSelected = listProductState.filter(line => line.selected);
    const listChangeTemp = [...listProductChange];
    if (listSelected.length) {
      listSelected.forEach(itemTouch => {
        const indexThisItemInListChange = listChangeTemp.findIndex(
          itemChange => itemChange.id === itemTouch.id
        );
        if (indexThisItemInListChange >= 0) {
          listChangeTemp[indexThisItemInListChange] = itemTouch;
        } else {
          listChangeTemp.push(itemTouch);
        }
      });
    }
    setListProductChange(listChangeTemp);
  };

  const handleNextPage = () => {
    beforeChangePage();
    loadNextPage();
  };

  const handlePreviousPage = () => {
    beforeChangePage();
    loadPreviousPage();
  };

  const getListAll = (countSelected = false) => {
    const listSelectedFromState = countSelected
      ? listProductState.filter(item => item.selected)
      : listProductState;
    const listIdSelectedState = listSelectedFromState.map(item => item.id);
    const listNotDuplicate = listProductChange
      .filter(item => (countSelected ? item.selected : true))
      .filter(item => {
        const indexOf = listIdSelectedState.indexOf(item.id);
        return indexOf < 0;
      });
    return [...listSelectedFromState, ...listNotDuplicate];
  };

  const handleClear = () => {
    const list = [...listProductState];
    list.forEach(item => {
      item.selected = false;
      item.quantity = "";
      item.errorQuantity = false;
    });
    setListProductState(list);
    setListProductChange([]);
  };

  const getDisableButton = () => {
    const listAll = getListAll(true);
    return !listAll.filter(item => item.selected || item.quantity).length;
  };

  // Submit
  const listSelectedAll = getListAll(true);

  const [
    addToCart,
    { loading: loadingAddToCart }
  ] = useCreateExportCartMutation({
    onCompleted: data => {
      if (data?.createExportCart?.cart?.id) {
        setTypeAlert("success");
        setOpenAlert(true);
        refetchCartExportCount().then(response => reCountCartExport(response));
        refetchExportPi();
      } else {
        setTypeAlert("error");
        setOpenAlert(true);
      }
    }
  });

  const toggleCloseCreateOrderError = () => {
    setOpenCreateOrderError(val => !val);
  };

  const [
    addToOrder,
    { loading: loadingCreateOrder }
  ] = useCreateExportOrderMutation({
    onCompleted: data => {
      if (data?.createExportOrder?.order?.id) {
        navigate(orderCartlUrl(data?.createExportOrder?.order?.id));
      } else if (
        ScgpExportErrorCode.PRODUCT_GROUP_ERROR ===
        data?.createExportOrder?.errors?.[0].code
      ) {
        setPopUpContent(
          <MyTypography variant="body2" className={classes.inlineBlock}>
            กรุณาเลือก{" "}
            <MyTypography variant="subtitle2" className={classes.inlineBlock}>
              สินค้ากลุ่มเดียวกัน
            </MyTypography>{" "}
            ในการสร้างคำสั่งซื้อ
          </MyTypography>
        );
        setOpenDialogError(true);
      } else {
        showNotifyMessage(notify, data?.createExportOrder?.errors, [], [], []);
      }
    }
  });

  const handleAddToCart = () => {
    const isError = checkBeforeSubmit();
    if (!isError) {
      addToCart({
        variables: {
          input: {
            piId: data?.exportPi?.id,
            soldToId: data.exportPi?.soldTo?.id,
            lines: listSelectedAll.map(item => ({
              quantity: item.quantity,
              piProductId: item.id
            }))
          }
        }
      }).then(response => {
        if (response?.data?.createExportCart?.cart?.id) {
          const newListProduct = [...listProductState];
          listSelectedAll.forEach(item => {
            const productDeduct = newListProduct.find(
              newItem => newItem.id === item.id
            );
            productDeduct.limitQuantity -= +productDeduct.quantity;
          });
          setListProductState(newListProduct);
        }
      });
    }
  };

  const handleCreateNow = () => {
    const isError = checkBeforeSubmit();
    if (!isError) {
      addToOrder({
        variables: {
          input: {
            orderHeader: {
              piId: data?.exportPi?.id
            },
            lines: listSelectedAll.map(item => ({
              quantity: item.quantity,
              piProduct: item.id
            }))
          }
        }
      });
    }
  };

  const checkBeforeSubmit = () => {
    const isLineError = listSelectedAll.some(item => item.errorQuantity);
    if (isLineError) {
      setOpenDialogError(true);
    }
    return isLineError;
  };

  const loadingPage = useMemo(
    () => loading || loadingCreateOrder || loadingAddToCart,
    [loading, loadingCreateOrder, loadingAddToCart]
  );

  return (
    <>
      <ContractNoExport data={data?.exportPi} />
      <TableDetail
        listProduct={listProductState}
        setListProduct={setListProductState}
        listProductChange={listProductChange}
        setListProductChange={setListProductChange}
        totalCount={data?.exportPi?.piProducts?.totalCount || 0}
        pageInfo={pageInfo}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        settings={settings}
        onUpdateListSettings={updateListSettings}
        disabled={loading}
        onRowClick={() => null}
        latestPageItemNumber={data?.exportPi?.piProducts?.latestPageItemNumber}
        onNextLastPage={handleNextLastPage}
        onPreviousFirstPage={handlePreviousFirstPage}
      />
      <BottomBar
        selectedLine={listSelectedAll.length}
        onClear={handleClear}
        disabled={getDisableButton() || loadingPage}
        onAddToCart={handleAddToCart}
        onCreateNow={handleCreateNow}
      />
      <DialogError
        onClose={() => {
          setOpenDialogError(false);
          setPopUpContent(null);
        }}
        open={openDialogError}
        body={popUpContent}
      />
      <SnackbarAlert
        severity={typeAlert}
        open={openAlert}
        onClose={() => setOpenAlert(false)}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        verticalCenter
      >
        <div className={classes.snackHeadTitle}>
          <MyTypography
            variant="subtitle2"
            style={{ color: "#ffffff", marginRight: "2px" }}
          >
            {intl.formatMessage({
              defaultMessage: "Added to cart",
              description: "dialog header"
            })}
          </MyTypography>
          <MyTypography variant="body3" style={{ color: "#ffffff" }}>
            {typeAlert === "success"
              ? "เพิ่มสินค้าลงตะกร้าสำเร็จ"
              : "เพิ่มสินค้าลงตะกร้าไม่สำเร็จ"}
          </MyTypography>
        </div>
      </SnackbarAlert>
      <Backdrop open={openAlert} className={classes.backDrop} />
      <DialogCreateOrderError
        onConfirm={toggleCloseCreateOrderError}
        open={openCreateOrderError}
        okText="ปิดหน้าต่าง"
        title={
          isStaff ? "ลูกค้ารายนี้ติด Credit Block" : "ไม่สามารถดำเนินการต่อได้"
        }
        content={
          isStaff ? (
            <MyTypography variant="body2" className={classes.inlineBlock}>
              เนื่องจากลูกค้ารายนี้ติด Credit Block กรุณาติดต่อฝ่ายสินเชื่อ
            </MyTypography>
          ) : (
            <span>
              <MyTypography variant="body2" className={classes.inlineBlock}>
                เนื่องจาก Customer Credit :
              </MyTypography>{" "}
              <MyTypography variant="subtitle2" className={classes.inlineBlock}>
                Credit Blocked
              </MyTypography>
            </span>
          )
        }
      />
    </>
  );
};

export default ContractDetail;
